import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import pxToRem from 'assets/theme/function/px-to-rem';
import borders from 'assets/theme/base/borders';
import colors from 'assets/theme/base/colors';
import { useLazyItemListQuery } from 'store/slices/product/product-item';
import { useEffect, useState } from 'react';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useAppDispatch } from 'hooks/redux';
import { RowDataType, TableMetaData } from 'types/common/common';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import { PriceInfo } from 'types/product/product-price';
import { useItemDeleteMutation } from 'store/slices/product/product-item';
import CreateProductItem from 'pages/product/product-detail-page/product-item/add-product-item';
import EditProductItem from 'pages/product/product-detail-page/product-item/edit-product-item';
import { ReactComponent as Plus } from 'assets/images/plus.svg';
import dayjs from 'dayjs';
import rgba from 'assets/theme/function/rgba';

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'itemType',
        position: 1,
        title: 'Item Type',
        isSortable: false,
    },

    {
        key: 'createdDate',
        position: 6,
        title: 'Date',
        isSortable: false,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },

    {
        key: 'action',
        position: 7,
        title: 'Action',
        isSortable: false,
    },
];

interface Props {
    dataProductId?: string;
    productId?: string;
    viewProduct?: string;
}
const ProductItem: React.FC<Props> = (props: Props) => {
    const { productId, dataProductId, viewProduct } = props;
    const [tableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [openDialogEditProductItem, setOpenDialogEditProductItem] =
        React.useState(false);
    const [productItemId, setProductItemId] = React.useState<string>('');
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [openDialogAddProductItem, setOpenDialogAddProductItem] =
        React.useState(false);

    const dispatch = useAppDispatch();

    const [userItemList, { data }] = useLazyItemListQuery();

    const [
        itemDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = useItemDeleteMutation();

    const getItemList = () => {
        userItemList(
            productId
                ? productId
                : dataProductId
                  ? dataProductId
                  : viewProduct
                    ? viewProduct
                    : '',
        );
    };

    const createNewItem = () => {
        setOpenDialogAddProductItem(true);
    };

    const deleteItem = (id: PriceInfo) => {
        itemDeleteApi(id.id);
    };
    const handleClickEditItem = (id: string) => {
        setOpenDialogEditProductItem(true);
        setProductItemId(id);
    };

    useEffect(() => {
        const tableData: RowDataType[] = data
            ? data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      action: (
                          <Box
                              display={'flex'}
                              justifyContent={'center'}
                              gap={2}
                          >
                              <Tooltip title="Delete">
                                  <Delete
                                      cursor="pointer"
                                      onClick={() => {
                                          dispatch(
                                              openConfirmationDialogs({
                                                  textInfo:
                                                      'Are You Sure Want To Delete Card',
                                                  handleConfirm: deleteItem,
                                                  parameters: {
                                                      id: fullInfo.id,
                                                  },
                                              }),
                                          );
                                      }}
                                  />
                              </Tooltip>
                              <Tooltip title="Edit">
                                  <Edit
                                      cursor="pointer"
                                      onClick={() =>
                                          handleClickEditItem(fullInfo.id)
                                      }
                                  />
                              </Tooltip>
                          </Box>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        getItemList();
    }, [productId, dataProductId]);

    useEffect(() => {
        if (deleteIsSuccess || productId) {
            setOpenDialogAddProductItem(false);
            getItemList();
        }
    }, [deleteIsSuccess, productId]);

    useEffect(() => {
        const error = deleteError;
        if (error && 'data' in error) {
            dispatch(
                openSnackbar({
                    message: error?.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
        if (deleteIsSuccess) {
            dispatch(
                openSnackbar({
                    message: productId ? 'Product Update' : 'Product Created',
                    severity: 'info',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [deleteIsError, deleteIsSuccess]);

    return (
        <>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <Box
                    display={'flex'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                    p={1}
                >
                    <Typography variant="h6" pl={pxToRem(10)}>
                        Product Item
                    </Typography>

                    <Button onClick={createNewItem} size={'small'}>
                        <Box
                            p={pxToRem(5)}
                            display={'flex'}
                            alignItems={'center'}
                        >
                            <Plus />
                            <Typography
                                pl={pxToRem(10)}
                                color={rgba(colors.primary.main, 1)}
                                variant={'subtitle1'}
                            >
                                Create New Item
                            </Typography>
                        </Box>
                    </Button>
                </Box>

                <TableContainer
                    sx={{
                        minHeight: pxToRem(150),
                        maxHeight: pxToRem(300),
                        overflowY: 'auto',
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                {tableMetaDataList.map((col) => (
                                    <TableCell key={col.key}>
                                        {col.title}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.map((row, idx) => (
                                <StyledTableRow key={idx} data={row}>
                                    {tableMetaDataList.map((col) => (
                                        <TableCell key={col.key}>
                                            {col.key === 'createdDate' &&
                                            row[col.key]
                                                ? dayjs(row[col.key]).format(
                                                      'DD.MM.YYYY',
                                                  )
                                                : row[col.key]}
                                        </TableCell>
                                    ))}
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Dialog
                open={openDialogAddProductItem}
                onClose={() => setOpenDialogAddProductItem(false)}
            >
                <CreateProductItem
                    productId={productId}
                    getItemList={getItemList}
                    setOpenDialogAddProductItem={setOpenDialogAddProductItem}
                    dataProductId={dataProductId}
                />
            </Dialog>

            {productItemId && (
                <Dialog
                    open={openDialogEditProductItem}
                    onClose={() => setOpenDialogEditProductItem(false)}
                >
                    <EditProductItem
                        productItemId={productItemId}
                        getItemList={getItemList}
                        setOpenDialogEditProductItem={
                            setOpenDialogEditProductItem
                        }
                    />
                </Dialog>
            )}
        </>
    );
};

export default ProductItem;
